import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LockOutlined } from "@ant-design/icons";
import { App, Button, Divider, Form, Input, Modal, Select, Spin, theme } from "antd";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { ChangePlanOrderSummary } from "./changePlanOrderSummary.js";
import { CustomerTermsSection } from "../../../../components/customerTermsSection.js";
import { RealCommerceError } from "../../../../exceptions/realCommerceError.js";
import { trackAnalyticsEvent } from "../../../../others/tracking.js";
import { useStores } from "../../../../store/stores.js";
import { formatAmount } from "../../../../utils/formatAmount.js";
import { __ } from "../../../../utils/i18n.js";
import { useAccountV2ErrorHandling } from "../../hooks/useAccountV2ErrorHandling.js";
import { usePlanList } from "../../hooks/usePlanList.js";
const ChangePlanModal = observer(({ subscription, autoSelectFirstUpgrade = false })=>{
    const { token } = theme.useToken();
    const { message, modal: antdModal } = App.useApp();
    const { accountV2Store } = useStores();
    const handleRealCommerceError = useAccountV2ErrorHandling();
    const [isBusy, setIsBusy] = useState(false);
    const [isCouponInputOpen, setIsCouponInputOpen] = useState(false);
    const [couponInput, setCouponInput] = useState("");
    const [hasAutoSelected, setHasAutoSelected] = useState(false);
    const [form] = Form.useForm();
    const planList = usePlanList(subscription);
    const { currency } = subscription.data;
    const selectedPlanId = Form.useWatch("productPlanId", form);
    const selectedPlan = useMemo(()=>subscription.changePlan.find((option)=>option.productPlan.id === selectedPlanId), [
        subscription.changePlan,
        selectedPlanId
    ]);
    const canApplyCoupon = selectedPlan?.type === "upgrade";
    const planOptions = useMemo(()=>subscription.changePlan.map((option)=>({
                value: option.productPlan.id,
                label: /*#__PURE__*/ _jsxs("span", {
                    children: [
                        option.productPlan.name,
                        ": ",
                        option.productPlan.description
                    ]
                })
            })), [
        subscription.changePlan
    ]);
    // When opened via the "Upgrade plan" promo (not the Actions overflow), pre-select the
    // next-larger plan so the customer lands on a concrete offer instead of an empty Select.
    // Gates on `planList.isLoading` because `subscription.changePlan` is a stable MobX-array
    // reference even after reassignment — useEffect would otherwise never re-run.
    useEffect(()=>{
        if (!autoSelectFirstUpgrade || hasAutoSelected || planList.isLoading) {
            return;
        }
        const firstUpgrade = subscription.changePlan.find((option)=>option.type === "upgrade");
        if (firstUpgrade) {
            form.setFieldValue("productPlanId", firstUpgrade.productPlan.id);
            setHasAutoSelected(true);
        }
    }, [
        autoSelectFirstUpgrade,
        hasAutoSelected,
        planList.isLoading,
        subscription,
        form
    ]);
    useEffect(()=>{
        if (!selectedPlanId || !selectedPlan) {
            return;
        }
        trackAnalyticsEvent("change_plan_plan_selected", {
            ga: {
                event_category: "ecommerce",
                event_label: "Change plan plan selected",
                value: selectedPlan.type
            },
            matomo: {
                category: "ecommerce",
                name: "Change plan plan selected",
                value: selectedPlan.type
            }
        });
    }, [
        selectedPlanId,
        selectedPlan
    ]);
    const closeImmediately = ()=>accountV2Store.closeSubscriptionModal();
    const confirmCancel = ()=>{
        if (!selectedPlanId) {
            closeImmediately();
            return;
        }
        antdModal.confirm({
            title: __("Cancel plan change?"),
            content: __("You'll lose your plan selection. Do you really want to cancel?"),
            okText: __("Yes, cancel"),
            cancelText: __("Keep changing plan"),
            onOk: closeImmediately
        });
    };
    const handleApplyCoupon = async ()=>{
        if (!couponInput) {
            return;
        }
        try {
            await planList.applyCoupon(couponInput);
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        }
    };
    const handleRemoveCoupon = async ()=>{
        try {
            await planList.removeCoupon();
            setCouponInput("");
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        }
    };
    const onFinish = async ({ productPlanId })=>{
        setIsBusy(true);
        try {
            await accountV2Store.applyChangePlan(subscription, productPlanId);
            trackAnalyticsEvent("change_plan_completed", {
                ga: {
                    event_category: "ecommerce",
                    event_label: "Change plan completed"
                },
                matomo: {
                    category: "ecommerce",
                    name: "Change plan completed"
                }
            });
            message.success(__("Your subscription has been updated."));
            closeImmediately();
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        } finally{
            setIsBusy(false);
        }
    };
    return /*#__PURE__*/ _jsx(Modal, {
        open: true,
        title: __("Change plan"),
        onCancel: confirmCancel,
        footer: null,
        destroyOnClose: true,
        maskClosable: !isBusy,
        width: 640,
        children: /*#__PURE__*/ _jsx(Spin, {
            spinning: planList.isLoading || isBusy || subscription.isBusy,
            children: /*#__PURE__*/ _jsxs(Form, {
                form: form,
                layout: "vertical",
                onFinish: onFinish,
                style: {
                    paddingTop: 8
                },
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        label: /*#__PURE__*/ _jsx("strong", {
                            children: __("Current plan")
                        }),
                        style: {
                            marginBottom: 12
                        },
                        children: /*#__PURE__*/ _jsxs("div", {
                            style: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "baseline",
                                gap: 12
                            },
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    style: {
                                        fontWeight: 500,
                                        color: token.colorTextHeading
                                    },
                                    children: [
                                        subscription.data.productPlan.name,
                                        ": ",
                                        subscription.data.productPlan.description
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        color: token.colorTextSecondary
                                    },
                                    children: subscription.currentRecurringPriceLabel ?? formatAmount({
                                        amount: Number(subscription.data.productPlan.price[0].recurringPrice),
                                        currency: subscription.data.productPlan.price[0].currency,
                                        paymentInterval: subscription.data.productPlan.paymentInterval,
                                        taxTreatment: subscription.taxTreatment
                                    })
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Divider, {
                        style: {
                            margin: "12px 0 20px"
                        }
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-change-plan__buy-area",
                        style: {
                            background: "rgba(69, 179, 221, 0.06)",
                            border: "1px solid rgba(69, 179, 221, 0.18)",
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 16
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Form.Item, {
                                label: /*#__PURE__*/ _jsx("strong", {
                                    children: __("New plan")
                                }),
                                name: "productPlanId",
                                rules: [
                                    {
                                        required: true,
                                        message: __("Please select a plan.")
                                    }
                                ],
                                style: {
                                    marginBottom: 12
                                },
                                children: /*#__PURE__*/ _jsx(Select, {
                                    loading: planList.isLoading,
                                    placeholder: __("Select a plan"),
                                    options: planOptions
                                })
                            }),
                            selectedPlan && /*#__PURE__*/ _jsx("div", {
                                style: {
                                    marginTop: 32
                                },
                                children: /*#__PURE__*/ _jsx(ChangePlanOrderSummary, {
                                    selectedPlan: selectedPlan,
                                    subscription: subscription,
                                    currency: currency,
                                    couponTrigger: canApplyCoupon && !isCouponInputOpen && !planList.appliedCoupon ? /*#__PURE__*/ _jsx(Button, {
                                        type: "link",
                                        size: "small",
                                        onClick: ()=>setIsCouponInputOpen(true),
                                        style: {
                                            padding: 0,
                                            fontSize: 13,
                                            fontWeight: "normal",
                                            textDecoration: "underline"
                                        },
                                        children: __("Got a coupon code?")
                                    }) : null,
                                    onRemoveCoupon: canApplyCoupon && planList.appliedCoupon ? ()=>handleRemoveCoupon() : undefined,
                                    couponSlot: canApplyCoupon && isCouponInputOpen && !planList.appliedCoupon ? /*#__PURE__*/ _jsxs("div", {
                                        style: {
                                            display: "flex",
                                            width: "100%"
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(Input, {
                                                style: {
                                                    flex: 1,
                                                    height: 32,
                                                    borderTopRightRadius: 0,
                                                    borderBottomRightRadius: 0
                                                },
                                                value: couponInput,
                                                onChange: (e)=>setCouponInput(e.target.value),
                                                onPressEnter: (e)=>{
                                                    e.preventDefault();
                                                    handleApplyCoupon();
                                                },
                                                placeholder: __("Enter your coupon code")
                                            }),
                                            /*#__PURE__*/ _jsx(Button, {
                                                type: "primary",
                                                style: {
                                                    height: 32,
                                                    borderTopLeftRadius: 0,
                                                    borderBottomLeftRadius: 0,
                                                    marginLeft: -1,
                                                    fontWeight: 600
                                                },
                                                onClick: handleApplyCoupon,
                                                children: __("Apply")
                                            })
                                        ]
                                    }) : null
                                })
                            })
                        ]
                    }),
                    selectedPlan && /*#__PURE__*/ _jsx(CustomerTermsSection, {
                        customerType: subscription.customerType,
                        seller: subscription.seller,
                        manufacturer: subscription.manufacturer,
                        additionalTeachings: (selectedPlan.productPlan.additionalTeachings ?? []).filter(({ teaching })=>teaching !== "cancellationPolicy" && teaching !== "exclusionOfRevocation")
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: 16,
                            gap: 12,
                            flexWrap: "wrap"
                        },
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                style: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "4px 10px",
                                    border: "1px solid #b7eb8f",
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: token.colorTextHeading
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(LockOutlined, {
                                        "aria-hidden": true,
                                        style: {
                                            color: token.colorSuccess,
                                            fontSize: 13
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        children: __("Secure, encrypted payment via Paddle")
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                type: "primary",
                                htmlType: "submit",
                                loading: isBusy,
                                disabled: !selectedPlan,
                                children: __("Change plan with payment obligation")
                            })
                        ]
                    })
                ]
            })
        })
    });
});
export { ChangePlanModal };
